import React from 'react';
import { Wrench } from 'lucide-react';
import { typography, palette } from '../../../../utils/designTokens';
import JSONCollapsibleViewer from './JSONCollapsibleViewer';

const resolveToolDefinition = (schema) => {
  if (!schema || typeof schema !== 'object') return { name: null, description: null, properties: {}, required: [] };
  const def = schema.function || schema;
  const name = def.name || schema.name || null;
  const description = def.description || null;
  const params = def.parameters || def.input_schema || {};
  const properties = params.properties || {};
  const required = params.required || [];
  return { name, description, properties, required };
};

const formatSchemaType = (prop) => {
  if (!prop) return '';
  if (prop.enum) return prop.enum.join(' | ');
  if (prop.type === 'array' && prop.items?.type) return `${prop.items.type}[]`;
  if (prop.type === 'array') return 'array';
  if (prop.anyOf) return prop.anyOf.map(t => t.type || 'any').join(' | ');
  return prop.type || '';
};

const parseMaybeJson = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const normalizeToolSchemaInput = (tool) => {
  if (!tool) return null;

  if (typeof tool === 'string') {
    return {
      name: tool,
      parameters: { type: 'object', properties: {}, required: [] },
    };
  }

  if (typeof tool !== 'object') {
    return {
      name: String(tool),
      parameters: { type: 'object', properties: {}, required: [] },
    };
  }

  const explicitSchema = parseMaybeJson(tool.json_schema || tool.tool?.json_schema || tool.schema);
  if (explicitSchema && typeof explicitSchema === 'object') return explicitSchema;

  const name = tool.name || tool.tool_name || tool.id || 'Tool';
  const description = tool.description || '';
  const paramsLike = parseMaybeJson(tool.parameters || tool.args || tool.input_schema || tool.properties);

  if (paramsLike && typeof paramsLike === 'object') {
    const hasJsonSchemaShape = paramsLike.type || paramsLike.properties || paramsLike.required || paramsLike.anyOf || paramsLike.items;
    return {
      name,
      description,
      parameters: hasJsonSchemaShape
        ? paramsLike
        : { type: 'object', properties: paramsLike, required: [] },
    };
  }

  return {
    name,
    description,
    parameters: { type: 'object', properties: {}, required: [] },
  };
};

const ToolSchemaCard = ({ schema, showHeader = true, framed = true }) => {
  if (!schema) return null;
  const { name, description, properties, required } = resolveToolDefinition(schema);
  const paramKeys = Object.keys(properties);

  if (!name && paramKeys.length === 0 && !description) {
    return <JSONCollapsibleViewer data={schema} />;
  }

  const body = (
    <div className="px-2.5 py-1.5">
      {description && (
        <div className="mb-1.5" style={{ fontSize: typography.size.xs, color: palette.text.secondary }}>
          {description}
        </div>
      )}
      {paramKeys.length > 0 && (
        <div className="space-y-1">
          {paramKeys.map((key) => {
            const prop = properties[key];
            const isRequired = required.includes(key);
            return (
              <div key={key} className="flex gap-2" style={{ fontSize: typography.size.xs }}>
                <span className="font-mono flex-shrink-0" style={{ color: palette.text.muted }}>
                  {key}{isRequired ? '*' : ''}:
                </span>
                <span className="font-mono" style={{ color: palette.text.primary }}>
                  {formatSchemaType(prop)}
                </span>
                {prop?.description && (
                  <span style={{ color: palette.text.supporting }}>
                    — {prop.description}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (!framed) {
    return body;
  }

  return (
    <div
      className="rounded border"
      style={{ borderColor: palette.borders.subtle, backgroundColor: palette.neutrals.canvas }}
    >
      {showHeader && (
        <div
          className="flex items-center gap-1.5 px-2.5 py-1.5 border-b"
          style={{ borderColor: palette.borders.subtle }}
        >
          <Wrench size={12} style={{ color: palette.accents.warm.base, flexShrink: 0 }} />
          <span
            className="font-mono font-semibold"
            style={{ fontSize: typography.size.xs, color: palette.accents.warm.base }}
          >
            {name || 'Unnamed tool'}
          </span>
        </div>
      )}
      {body}
    </div>
  );
};

export default ToolSchemaCard;
